import Container from "components/general/Container";
import ScrollContainer from "components/general/ScrollContainer";
import ServicesItem from "components/general/ServicesItem";
import { Tab, TabContainer } from "components/general/Tabs";
import TrainingItem from "components/general/TrainingItem";
import WebContentItem from "components/general/WebContentItem";
import ItemListItem from "components/item/ItemListItem";
import Pagination from "components/navigation/Pagination";
import SiteItem from "components/sites/SiteItem";
import { Box, H1, H4, Link, Typography } from "components/ui";
import { SearchX } from "lucide-react-native";
import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from "react";
import { ActivityIndicator, ScrollView } from "react-native";
import { Container as GridContainer } from "react-native-flex-grid";
import { FlatList } from "react-native-gesture-handler";
import ItemListing from "screens/ItemListing";
import { ItemAvailability, useGetSearchQuery } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { useSiteSearch } from "utils/hooks/useSiteSearch";

// Memoized sub-components to prevent unnecessary re-renders
const MemoizedItemListItem = memo(ItemListItem);
const MemoizedSiteItem = memo(SiteItem);
const MemoizedTrainingItem = memo(TrainingItem);
const MemoizedServicesItem = memo(ServicesItem);
const MemoizedWebContentItem = memo(WebContentItem);

// Extracted site renderer as a separate component
const SiteListItem = memo(({ site, onClose }: any) => {
	return (
		<Link
			href={"/site/" + site.id}
			key={site.id}
			onPress={onClose}
			component={(hovered) => {
				const styleData = {
					marginLeft: 0,
					marginRight: 0,
					paddingHorizontal: 16,
					paddingVertical: 18,
					marginBottom: 14,
					...(hovered && { backgroundColor: "#E1E9F0" }),
				};
				return <MemoizedSiteItem isOnSearch data={site} styleData={styleData} />;
			}}
		/>
	);
});

// Extracted items section as a separate component
const ItemsSection = memo(({ items }: any) => {
	const keyExtractor = useCallback((item: any) => item.stockcode, []);
	const renderItem = useCallback(({ item }: any) => <MemoizedItemListItem isOnSearch item={item} />, []);

	return (
		<Box bg="$neutral25">
			<FlatList data={items?.slice(0, 4)} renderItem={renderItem} keyExtractor={keyExtractor} removeClippedSubviews={true} />
		</Box>
	);
});

// Extracted sites section as a separate component
const SitesSection = memo(({ sites, onClose }: any) => {
	return (
		<Box bg="$neutral25">
			<GridContainer
				noPadding
				style={{
					marginLeft: 0,
					width: "100%",
					maxWidth: "100%",
					marginBottom: 16,
				}}
			>
				{sites?.slice(0, 4).map((site: any) => <SiteListItem key={site.id} site={site} onClose={onClose} />)}
			</GridContainer>
		</Box>
	);
});

// Extracted training section as a separate component
const TrainingSection = memo(() => (
	<Box bg="$neutral25">
		<MemoizedTrainingItem />
		<MemoizedTrainingItem />
		<MemoizedTrainingItem />
	</Box>
));

// Extracted services section as a separate component
const ServicesSection = memo(() => (
	<Box bg="$neutral25">
		<MemoizedServicesItem />
		<MemoizedServicesItem />
		<MemoizedServicesItem />
	</Box>
));

// Extracted web content section as a separate component
const WebContentSection = memo(() => (
	<Box bg="$neutral25">
		<MemoizedWebContentItem title="Meet the Team: Cleaning Experts" type="web" />
		<MemoizedWebContentItem title="Meet the Team: Cleaning Experts" type="blog" />
		<MemoizedWebContentItem title="Meet the Team: Cleaning Experts" type="blog" />
		<MemoizedWebContentItem title="Meet the Team: Cleaning Experts" type="web" />
		<MemoizedWebContentItem title="Meet the Team: Cleaning Experts" type="faq" />
		<MemoizedWebContentItem title="Meet the Team: Cleaning Experts" type="web" />
	</Box>
));

export default function Page() {
	const { searchTerm } = useSiteSearch();
	const [filterValues, setFilterValues] = useState<any>({
		pageNumber: 1,
		pageSize: 24,
	});
	const [activeTab, setActiveTab] = useState("all");
	const scrollRef = useRef<ScrollView>(null);
	const isMobile = useIsMobile();

	const {
		data: itemSearchResults,
		isLoading: itemIsLoading,
		isFetching: itemIsFetching,
	} = useGetSearchQuery(
		{
			searchTerm: searchTerm,
			...filterValues,
		},
		{
			skip: searchTerm.length === 0,
		},
	);

	const {
		data: siteSearchResults,
		isLoading: siteIsLoading,
		isFetching: siteIsFetching,
	} = useGetSearchQuery(
		{
			searchTerm: searchTerm,
			...filterValues,
		},
		{
			skip: searchTerm.length === 0,
		},
	);

	// Memoize derived values
	const itemCount = useMemo(() => itemSearchResults?.itemCollection?.list?.length || 0, [itemSearchResults?.itemCollection?.list]);
	const siteCount = useMemo(() => siteSearchResults?.siteCollection?.list?.length || 0, [siteSearchResults?.siteCollection?.list]);
	const hasSites = useMemo(() => siteCount > 0, [siteCount]);
	const totalCount = useMemo(() => itemCount + siteCount, [itemCount, siteCount]);

	// Close callback (memoized)
	const handleClose = useCallback(() => {
		// Implementation of close function
	}, []);

	// Handle filter changes
	function handleFilterChange(value: any) {
		setFilterValues((prevValues: any) => ({
			...prevValues,
			...value,
		}));
	}

	// Handle page changes
	const handlePageChange = useCallback((pageNumber: number) => {
		setFilterValues((prevValues: any) => ({
			...prevValues,
			pageNumber,
		}));

		// Scroll to top when page changes
		if (scrollRef.current) {
			scrollRef.current.scrollTo({ y: 0, animated: true });
		}
	}, []);

	// Reset pagination to page 1 when search term changes
	useEffect(() => {
		if (searchTerm.length > 0) {
			setFilterValues((prevValues: any) => ({
				...prevValues,
				pageNumber: 1,
			}));
		}
	}, [searchTerm]);

	// Cleanup function for scroll ref
	useEffect(() => {
		return () => {
			// Cleanup any scroll-related state
		};
	}, []);

	// Add a derived loading state that considers both the query state and whether we have data
	const isActuallyLoading = useMemo(() => {
		if (searchTerm.length === 0) return false;
		return itemIsLoading || !itemSearchResults;
	}, [itemIsLoading, searchTerm, itemSearchResults]);

	// Only show content when we have actual data
	const shouldShowContent = useMemo(() => {
		return !isActuallyLoading && (itemSearchResults?.itemCollection?.list?.length ?? 0) > 0;
	}, [isActuallyLoading, itemSearchResults]);

	// Show tab when we have search term and not loading (even if no results)
	const shouldShowProductsTab = useMemo(() => {
		return searchTerm.length > 0 && !isActuallyLoading;
	}, [searchTerm, isActuallyLoading]);

	return (
		<ScrollContainer scrollRef={scrollRef}>
			<Container sx={{ padding: isMobile ? 20 : undefined }}>
				<Box py={24}>{searchTerm ? <H1 sx={{ fontFamily: "Roboto_700Bold" }}>You are searching for: {searchTerm}</H1> : <H1>No search term</H1>}</Box>
				<TabContainer>
					{shouldShowProductsTab && (
						<Tab id="products" title="Products & Equipment">
							{isActuallyLoading ? (
								<Box flex={1} justifyContent="center" alignItems="center" minHeight={300}>
									<ActivityIndicator size="large" style={{ transform: [{ scaleX: 2 }, { scaleY: 2 }] }} />
								</Box>
							) : itemSearchResults?.itemCollection?.list ? (
								<>
									<ItemListing
										isOnSearch
										topCategory={null}
										category={null}
										isFetchingItems={itemIsFetching}
										handleFilterChange={handleFilterChange}
										itemCollection={itemSearchResults.itemCollection.list}
										totalFoundItems={itemSearchResults.itemCollection.metaData.totalItemCount}
									/>
									{!itemIsFetching && (
										<Box justifyContent="center" bg="$neutral25" alignItems="center" py={24}>
											<Pagination pageCount={Math.ceil((itemSearchResults.itemCollection.metaData.totalItemCount || 0) / filterValues.pageSize)} pageNumber={filterValues.pageNumber} onChangePage={handlePageChange} />
										</Box>
									)}
								</>
							) : (
								<Box flex={1} justifyContent="center" alignItems="center" minHeight={300}>
									<SearchX size={100} color={"black"} />
									<Typography.H5>Oops! No item found.</Typography.H5>
								</Box>
							)}
						</Tab>
					)}

					{hasSites && (
						<Tab id="sites" title="Sites">
							<Box bg="$neutral25">
								<GridContainer
									noPadding
									style={{
										marginLeft: 0,
										width: "100%",
										maxWidth: "100%",
										marginBottom: 16,
									}}
								>
									{hasSites ? (
										siteSearchResults?.siteCollection?.list?.map((site: any) => (
											<Box key={site.id}>
												<SiteListItem site={site} onClose={handleClose} />
											</Box>
										))
									) : (
										<H4>Nothing found.</H4>
									)}
								</GridContainer>
							</Box>
						</Tab>
					)}
				</TabContainer>
			</Container>
		</ScrollContainer>
	);
}
